/**
 * The invoice dashboard's presentational chrome (Rechnungen 2.0 — R9).
 *
 * THESE THREE PIECES WERE INLINE IN THE PAGE and are MOVED here unchanged. The
 * page had grown to 579 lines while carrying an 82-line exclusions card, a
 * creation CTA and a tab row on top of its actual job — orchestrating six hooks
 * and composing one of two workspaces. CLAUDE.md Regel 48 asks for exactly this
 * evaluation before the next feature lands on top, and the answer here is a
 * single presentational leaf rather than three files or a shared-primitive
 * layer: the pieces are used by one page, together, and splitting them further
 * would trade one real problem for three imports.
 *
 * NOTHING HERE DECIDES ANYTHING. No hook, no state, no effect, no request. Each
 * component is handed what the page already read and renders it. That is what
 * makes the move safe to reason about: if a figure is wrong on screen, it was
 * wrong before it got here.
 *
 * The exclusions card still recomputes nothing. The server partitions the
 * excluded population and this renders that partition in the order the product
 * speaks it; a frontend that re-classified a row against its own idea of
 * "draft" would produce a second, quietly different answer to the question the
 * four KPI cards above it already answer.
 */
import React from "react";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { fmtInvoiceMoney } from "../../../components/admin/bexio/bexioFormat";

const NATIVE_CREATE_ROUTE = "/admin/invoices/new";
const BEXIO_DRAFT_ROUTE = "/admin/bexio/drafts";

/** The card shell the dashboard's panels share, including the page's own. */
export const INVOICE_CARD =
  "rounded-xl border border-border bg-card p-4 text-card-foreground shadow-sm";

const PRIMARY_ACTION =
  "inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-3 py-2 text-sm font-medium text-primary-foreground no-underline transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Why rows sat outside the four cards, in the server's own words.
 *
 * Only the reasons a given source CAN have are listed: a row this application
 * created is issued by definition and no provider could have lost it, so the
 * native side has neither `draft` nor `provider_missing`. Reading the keys off
 * the response rather than off a fixed list is what keeps that true.
 */
const EXCLUDED_ORDER = [
  ["provider_missing", "Bei Bexio nicht mehr auffindbar"],
  ["draft", "Entwürfe"],
  ["cancelled", "Storniert"],
  ["unknown_status", "Status unbekannt"],
  ["unknown_currency", "Währung unbekannt"],
];

export const invoiceCount = (n) => `${n} ${n === 1 ? "Rechnung" : "Rechnungen"}`;

/**
 * The two workspaces of one invoice world.
 *
 * Triggers only, no `TabsContent`: Radix mounts every panel it is given, and
 * mounting both is exactly how a page ends up holding two workspaces — and two
 * sets of requests — at once. The page renders the chosen one beside them, the
 * same division `InvoiceSourceSwitcher` uses.
 */
export function InvoiceViewSwitcher({ view, onSelect }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* R9: the accessible name has always been right; the EYE had nothing.
          Two identically styled pill rows sat on top of each other and the
          reader had to infer from the words which one chose a world and which
          one chose a workspace. The label is visible, muted, and belongs to
          the switch it names. */}
      <span
        id="invoice-view-switch-label"
        className="text-[11px] font-medium uppercase tracking-[0.6px] text-muted-foreground"
      >
        Ansicht
      </span>
      <Tabs value={view} onValueChange={onSelect}>
        <TabsList aria-label="Ansicht">
          <TabsTrigger value="invoices" className="min-h-[32px] px-4">
            Rechnungen
          </TabsTrigger>
          <TabsTrigger value="payments" className="min-h-[32px] px-4">
            Zahlungen
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );
}

/**
 * The excluded population, never recomputed here.
 *
 * One row, one reason: the server partitions the exclusions, so the counts can
 * be read side by side. A frontend that re-classified a row against its own idea
 * of "draft" would produce a second, quietly different answer.
 */
export function InvoiceDataQualityCard({ summary, loading, error }) {
  const excluded = summary?.excluded;
  const reasons = excluded
    ? EXCLUDED_ORDER.filter(
        ([key]) => excluded[key] != null && excluded[key].count > 0
      )
    : [];
  const foreign = Array.isArray(excluded?.non_chf)
    ? excluded.non_chf.filter((b) => b.count > 0)
    : [];

  return (
    <section
      data-testid="invoice-data-quality"
      aria-labelledby="invoice-data-quality-heading"
      aria-busy={loading || undefined}
      className={INVOICE_CARD}
    >
      <h2
        id="invoice-data-quality-heading"
        className="text-[13px] font-semibold text-card-foreground"
      >
        Abgleich &amp; Datenqualität
      </h2>

      {loading ? (
        <p className="mt-3 text-[12px] text-muted-foreground">
          Ausschlüsse werden geladen …
        </p>
      ) : error ? (
        <p className="mt-3 text-[12px] text-destructive">
          Ausschlüsse konnten nicht geladen werden.
        </p>
      ) : reasons.length === 0 && foreign.length === 0 ? (
        <p className="mt-3 text-[12px] text-muted-foreground">
          Keine ausgeschlossenen Rechnungen in dieser Auswahl.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {reasons.map(([key, label]) => (
            <li
              key={key}
              data-testid={`invoice-excluded-${key}`}
              className="flex items-center justify-between gap-3 py-2 first:pt-0"
            >
              <span className="min-w-0 text-[12px] text-card-foreground">{label}</span>
              <span className="shrink-0 font-mono text-[12px] text-muted-foreground">
                {excluded[key].count}
              </span>
            </li>
          ))}
          {foreign.map((bucket) => (
            <li
              key={bucket.currency}
              data-testid={`invoice-excluded-non-chf-${bucket.currency}`}
              className="flex items-center justify-between gap-3 py-2 first:pt-0"
            >
              <div className="min-w-0">
                <p className="m-0 text-[12px] text-card-foreground">
                  {bucket.currency}
                </p>
                {/* Summed in its own unit — never converted, never added to a
                    franc. There is no exchange-rate source in this product. */}
                <p className="m-0 text-[11px] text-muted-foreground">
                  {invoiceCount(bucket.count)}
                </p>
              </div>
              <span className="shrink-0 font-mono text-[12px] text-muted-foreground">
                {fmtInvoiceMoney(bucket.amount, bucket.currency)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Where invoices may be created, which is not the same question as where they
 * are being read. Nothing is offered while the answer is unknown.
 */
export function InvoiceCreationCta({ nativeCreationEnabled, provider }) {
  if (nativeCreationEnabled) {
    return (
      <Link className={PRIMARY_ACTION} to={NATIVE_CREATE_ROUTE}>
        <Plus aria-hidden="true" className="h-4 w-4" />
        Rechnung erstellen
      </Link>
    );
  }
  if (provider === "bexio") {
    return (
      <Link className={PRIMARY_ACTION} to={BEXIO_DRAFT_ROUTE}>
        <Plus aria-hidden="true" className="h-4 w-4" />
        Rechnungsentwürfe erstellen
      </Link>
    );
  }
  return null;
}
